import React, { useEffect, useState } from "react";
import { Order } from "../../interfaces/order";
import { Service } from "../../interfaces/service";
import { SimpleClient } from "../../interfaces/client";
import {
  formatDate,
  getSafePickerDate,
  getStates,
  isValidZip,
  sortServices,
  toSafeDateString,
} from "../../services/helpers";
import { InvoiceProcessType } from "../../interfaces/invoiceProcessType";
import { getInvoiceProcessTypes } from "../../services/invoiceProcessTypesService";
import { ReferenceType } from "../../interfaces/referenceType";
import { getReferenceTypes } from "../../services/referenceTypeService";
import { getAllServices } from "../../services/serviceService";
import ToolTip from "../../components/component-tools/ToolTip";
import {
  checkCandidateEmail,
  createCandidate,
  createCandidateSuran,
  getCandidateByEmail,
  sendFormsReadyEmailToCandidate,
} from "../../services/candidateService";
import {
  checkClientStatusByAbbreviation,
  checkForms,
  getClientsBySearch,
} from "../../services/clientService";
import { ReferenceOrder } from "../../interfaces/referenceOrder";
import { createOrdersAdmin } from "../../services/orderService";
import { useNavigate } from "react-router-dom";
import { CriminalOrder } from "../../interfaces/criminalOrder";
import {
  conflictRules,
  getPositionCodes,
  getServiceIDsForPositionCode,
} from "../../services/positionCodeService";
import MonthDayYearPicker from "../../components/component-tools/MonthDayYearPicker";
import { CandidateOrder } from "../../interfaces/candidate";

const CreateOrder = () => {
  var tempID = React.useRef(1);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [order, setOrder] = useState<Order>({
    orderID: 0,
    clientID: 0,
    candidateID: 0,
    initialFormsDate: null,
    initiatedDate: new Date(),
    receivedDate: null,
    designation: "",
    sendByMail: false,
    orderStatusID: 0,
    candidateSSN: "",
    candidateDOB: null,
    candidateTitle: "",
    candidateFirstName: "",
    candidateLastName: "",
    candidateMI: "",
    candidateEmail: "",
    candidatePhone: "",
    candidateAddress: "",
    candidateAddress2: "",
    candidateCity: "",
    candidateState: "",
    candidateZipCode: "",
    candidateCountry: "United States",
    fcraCompliance: true,
    updateMessage: "",
    invoiceProcessTypeID: 1,
    completedDate: null,
    lastUpdatedDate: new Date(),
    requiresCandidatePayment: false,
    paymentReceived: false,
    alert: "",
    alertLastUpdatedDate: null,
  });
  const emptyCandidateOrder: CandidateOrder = {
    candidateID: 0,
    candidateEmail: "",
    isActive: true,
    candidateOrderID: tempID.current++,
    candidateSSN: null,
    candidateDOB: null,
    candidateTitle: "",
    candidateFirstName: "",
    candidateLastName: "",
    candidateMI: null,
    candidatePhone: null,
    candidateAddress: null,
    candidateAddress2: null,
    candidateCity: null,
    candidateState: null,
    candidateCountry: null,
    candidateZipCode: null,
    candidateSSNLast4: null,
  };
  const [candidateOrders, setCandidateOrders] = useState<CandidateOrder[]>([
    emptyCandidateOrder,
  ]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<SimpleClient | null>();
  const states = getStates();
  const [invoiceProcessTypes, setInvoiceProcessTypes] = useState<
    InvoiceProcessType[]
  >([]);
  const [referenceTypes, setReferenceTypes] = useState<ReferenceType[]>([]);
  const [orderServices, setOrderServices] = useState<number[]>([]);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [referenceOrder, setReferenceOrder] = useState<ReferenceOrder>({
    referenceOrderID: 0,
    orderID: 0,
    years: 10,
    priorClientName: "",
    priorDate: undefined,
    referenceTypeID: 3,
    invoiceAmount: 0,
    sentToInvoicing: false,
    responseTrackingReceived: "",
    finalReportDate: undefined,
    finalReportUrl: "",
    invoiceDate: undefined,
    inAdminReview: false,
    canQCrimAdmit: false,
    retract: false,
  });
  const [criminalOrder, setCriminalOrder] = useState<CriminalOrder>({
    criminalOrderID: 0,
    orderID: 0,
    years: 7,
    sentToInvoicing: false,
    invoiceDate: undefined,
    finalReportDate: undefined,
    finalReportUrl: "",
    billedDate: undefined,
    inAdminReview: false,
  });
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [invoiceAmountCheck, setInvoiceAmountCheck] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [clientOptions, setClientOptions] = useState<SimpleClient[]>([]);
  const [positionCode, setPositionCode] = useState<number | null>(null);
  const [saveDisabled, setSaveDisabled] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // get all services
        try {
          const servicesResponse = await getAllServices();
          const sortedServices = await sortServices(servicesResponse);
          setServices(sortedServices);
        } catch (error) {
          console.error("Error fetching services:", error);
        }

        // get process types
        try {
          const types = await getInvoiceProcessTypes();
          setInvoiceProcessTypes(types);
        } catch (error) {
          console.error("Error fetching invoice process types:", error);
        }

        // get reference types
        try {
          let typesR = await getReferenceTypes();
          typesR = typesR.filter((t) => t.referenceTypeID !== 2);
          setReferenceTypes(typesR);
        } catch (error) {
          console.error("Error fetching reference types:", error);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setErrorMessage("Failed to load initial data.");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    candidateOrder?: CandidateOrder,
  ) => {
    let value: string | boolean | number =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (field === "referenceTypeID") {
      value = Number(value);

      if (value === 1 && candidateOrders.length > 1) {
        setSaveDisabled(true);
      } else {
        setSaveDisabled(false);
      }

      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        priorClientName: "",
        priorDate: "",
      }));
    }

    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));

    if (candidateOrder) {
      setCandidateOrders((prev) =>
        (prev ?? []).map((co) =>
          co.candidateOrderID === candidateOrder.candidateOrderID
            ? { ...co, [field]: value }
            : co,
        ),
      );
      setValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[`${field}-${candidateOrder.candidateOrderID}`];
        return updatedErrors;
      });
    } else if (order && field in order) {
      setOrder((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else if (referenceOrder && field in referenceOrder) {
      setReferenceOrder((prev) => ({
        ...prev!,
        [field]: value,
      }));
    }
  };

  const handleCriminalInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (criminalOrder && field in criminalOrder) {
      setCriminalOrder((prev) => ({
        ...prev!,
        [field]: value,
      }));
    }
  };

  const handleCheckCandidateEmail = async (candidateOrder: CandidateOrder) => {
    if (
      candidateOrder.candidateEmail === null ||
      candidateOrder.candidateEmail === ""
    ) {
      candidateOrder.newEmailText = "Enter an email";
      return;
    }

    try {
      setLoadingSpinner(true);
      const emailExists: boolean = await checkCandidateEmail(
        candidateOrder.candidateEmail,
      );
      if (emailExists) {
        candidateOrder.newEmailText =
          "Previously used email. Email is attached to another order.";
      } else {
        candidateOrder.newEmailText = "New Email.";
      }
    } catch {
      console.log("error check email");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleCheckboxChange = (serviceID: number) => {
    const isSelected = orderServices.includes(serviceID);
    const conflictingServices = conflictRules[serviceID] || [];

    //check for conflicts
    if (!isSelected) {
      const foundConflict = conflictingServices.find((conflictID) =>
        orderServices.includes(conflictID),
      );

      if (foundConflict) {
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          orderServices: `You have a conflicting selected service.`,
        }));
        return;
      }
    }

    if ([1, 2, 3].includes(serviceID)) {
      const matchedService = services.find(
        (service) => service.serviceID === serviceID,
      );
      referenceOrder.invoiceAmount = matchedService
        ? client?.referenceFeeTier === 1
          ? matchedService.fee
          : client?.referenceFeeTier === 2
            ? matchedService.fee2
            : 0
        : 0;
    }

    setOrderServices((prevOrderServices) => {
      if (isSelected) {
        return prevOrderServices.filter((id) => id !== serviceID);
      } else {
        return [...prevOrderServices, serviceID];
      }
    });

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      orderServices: "",
    }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    const isValidEmail = (email: string) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // Order Validations
    if (!client) {
      errors.clientAbbreviation = "Client is required.";
    } else {
      const clientStatus = await checkClientStatusByAbbreviation(
        client?.abbreviation ? client.abbreviation : "",
      );
      // check if client has necessary forms unloaded
      const [formsHere, message] = await checkForms(client?.clientID);
      if (!clientStatus) {
        errors.clientAbbreviation =
          "Client is not approved or not active, or questionnaires have not been approved. Do not create order.";
      } else if (!formsHere && message.includes("User Agreement")) {
        errors.clientAbbreviation = message;
      } else {
        delete errors.clientAbbreviation;
      }
    }

    if (orderServices.length === 0) {
      errors.orderServices = "You must select at least one service.";
    }
    if (!order.designation) {
      errors.designation = "Designation is required.";
    }
    if (
      orderServices.some((serviceID) => [1, 2, 3].includes(serviceID)) &&
      !invoiceAmountCheck
    ) {
      errors.invoiceAmountCheck =
        "Please Confirm the invoice amount is correct.";
    }
    if (
      referenceOrder.referenceTypeID === 1 &&
      !referenceOrder.priorClientName
    ) {
      errors.priorClientName = "Prior Client name is required.";
    }
    if (referenceOrder.referenceTypeID === 1 && !referenceOrder.priorDate) {
      errors.priorDate = "Prior Date name is required.";
    }

    candidateOrders.forEach((co) => {
      const key = co.candidateOrderID;

      if (!co.candidateFirstName?.trim())
        errors[`candidateFirstName-${key}`] =
          "Candidate first name is required.";

      if (!co.candidateLastName?.trim())
        errors[`candidateLastName-${key}`] = "Candidate last name is required.";

      // Email required if NOT sending by mail
      if (!order.sendByMail) {
        if (!co.candidateEmail?.trim())
          errors[`candidateEmail-${key}`] = "Candidate email is required.";
        else if (!isValidEmail(co.candidateEmail))
          errors[`candidateEmail-${key}`] = "A valid email is required.";
      }

      // Address required if sending by mail
      if (order.sendByMail) {
        if (
          !co.candidateAddress?.trim() ||
          !co.candidateCity?.trim() ||
          !co.candidateState?.trim() ||
          !co.candidateZipCode?.trim()
        ) {
          errors[`candidateAddress-${key}`] =
            "Need an address if sending by mail.";
        } else if (!isValidZip(co.candidateZipCode?.trim())) {
          errors[`candidateAddress-${key}`] = "Enter a valid ZipCode.";
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    try {
      setLoadingSpinner(true);

      const isValid = await validateFields();
      if (!isValid) {
        console.log("Validation failed. Please fix the errors.");
        setLoadingSpinner(false);
        const firstErrorElement = document.querySelector(".invalid-feedback");
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
        return;
      }

      if (!client) return;

      const resolvedCandidateOrders: CandidateOrder[] = await Promise.all(
        candidateOrders.map(async (co) => {
          const email = (co.candidateEmail ?? "").trim();

          // Mail flow: if no email, make an empty candidate
          if (order.sendByMail && !email) {
            const created = await createCandidateSuran();
            return {
              ...co,
              ...created,
              candidateID: created.candidateID,
              candidateEmail: created.candidateEmail ?? null,
            };
          }

          // Online flow
          if (email) {
            const exists = await checkCandidateEmail(email);

            const candidateResponse = exists
              ? await getCandidateByEmail(email)
              : await createCandidate(email);

            return {
              ...co,
              ...candidateResponse,
              candidateID: candidateResponse.candidateID,
              candidateEmail:
                candidateResponse.candidateEmail ?? co.candidateEmail,
            };
          }

          return co;
        }),
      );

      setCandidateOrders(resolvedCandidateOrders);

      const adminResponse = await createOrdersAdmin(
        client,
        order,
        resolvedCandidateOrders,
        orderServices,
        referenceOrder,
        criminalOrder,
      );

      if (!order.sendByMail) {
        await Promise.all(
          adminResponse.createdOrders.map(async (created: any) => {
            await sendFormsReadyEmailToCandidate(
              created.candidateFirstName,
              created.candidateLastName,
              created.candidateEmail,
              client.diocese ? client.diocese : "",
              client.clientID,
              created.orderID,
            );
          }),
        );
      }

      navigate("/admin/orders/all", {
        state: {
          successMessage: "Order(s) created successfully.",
          reload: true,
        },
      });
    } catch (error) {
      console.error("Error creating orders or candidates:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  // Debounce search term for Client
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.trim()) {
        fetchClients(searchTerm.trim());
      } else {
        setClientOptions([]);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchClients = async (search: string) => {
    try {
      delete validationErrors.clientAbbreviation;
      const clients = await getClientsBySearch(search);
      setClientOptions(clients);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
    }
  };

  const handlePositionCodeChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const raw = e.target.value;

    var numeric = raw === "" ? null : Number(raw);
    setPositionCode(numeric);

    if (client === undefined || client === null || numeric == null) return;

    try {
      var positionCodes = await getPositionCodes(client.clientID);

      numeric = numeric - 1;
      const foundPosCode = positionCodes[numeric];

      if (foundPosCode == null) {
        setOrderServices([]);
        return;
      }

      var posCodeServices = await getServiceIDsForPositionCode(
        foundPosCode.positionCodeID,
      );
      setOrderServices(posCodeServices);

      //set fee for pos code
      const refServiceIds = [1, 2, 3];

      posCodeServices.forEach((poscode: number) => {
        if (refServiceIds.includes(poscode)) {
          const matchedService = services.find(
            (service) => service.serviceID === poscode,
          );

          referenceOrder.invoiceAmount = matchedService
            ? client?.referenceFeeTier === 1
              ? matchedService.fee
              : client?.referenceFeeTier === 2
                ? matchedService.fee2
                : 0
            : 0;
        }
      });
      setOrder((prev) => ({
        ...prev,
        designation: foundPosCode.positionCodeTitle ?? "",
      }));
      setCriminalOrder((prev) => ({
        ...prev,
        years: foundPosCode.criminalLookbackYears ?? 7,
      }));
    } catch (error) {
      console.error("Error finding services:", error);
    }
  };

  const handleAddAnotherCandidateOrder = () => {
    if (
      referenceOrder &&
      referenceOrder.referenceTypeID === 1 &&
      candidateOrders.length === 1
    ) {
      setSaveDisabled(true);
      return;
    } else {
      setSaveDisabled(false);
    }

    if (referenceOrder)
      setCandidateOrders((prevList) => {
        return [
          ...prevList,
          {
            candidateID: 0,
            candidateEmail: "",
            isActive: true,
            candidateOrderID: tempID.current++,
            candidateSSN: null,
            candidateDOB: null,
            candidateTitle: "",
            candidateFirstName: "",
            candidateLastName: "",
            candidateMI: null,
            candidatePhone: null,
            candidateAddress: null,
            candidateAddress2: null,
            candidateCity: null,
            candidateState: null,
            candidateCountry: null,
            candidateZipCode: null,
            candidateSSNLast4: null,
          },
        ];
      });
  };

  const handleDeleteCandidateOrder = (candidateOrderID: number) => {
    setCandidateOrders((prevList) =>
      prevList.filter((c) => c.candidateOrderID !== candidateOrderID),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`candidateFirstName-${candidateOrderID}`) ||
          key.startsWith(`candidateLastName-${candidateOrderID}`) ||
          key.startsWith(`candidateEmail-${candidateOrderID}`) ||
          key.startsWith(`candidateAddress-${candidateOrderID}`) ||
          key.startsWith(`candidateCity-${candidateOrderID}`) ||
          key.startsWith(`candidateState-${candidateOrderID}`) ||
          key.startsWith(`candidateZipCode-${candidateOrderID}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });

    if (
      referenceOrder &&
      referenceOrder.referenceTypeID === 1 &&
      candidateOrders.length - 1 > 1
    ) {
      setSaveDisabled(true);
    } else {
      setSaveDisabled(false);
    }
  };

  return (
    <div className="content-container">
      {!loading && (
        <div className="header-container">
          <div
            className="page-items-container"
            style={{ alignItems: "center" }}
          >
            <h1>New Order</h1>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="data-container">
            {order && (
              <>
                <div
                  className="text-container-header"
                  style={{ flex: "0 0 100%" }}
                >
                  Client
                </div>
                <div style={{ flex: "0 0 50%", display: "flex" }}>
                  <div
                    className="search-container"
                    style={{
                      width: "100%",
                      position: "relative",
                      alignSelf: "end",
                    }}
                  >
                    <input
                      style={{ border: "none", width: "100%", outline: "none" }}
                      type="text"
                      placeholder="Choose Client ..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setClient(null);
                      }}
                    />
                    {searchTerm.trim() ? (
                      <i
                        className="bi bi-x-circle clear-icon"
                        onClick={() => {
                          setClient(null);
                          setSearchTerm("");
                          setClientOptions([]);
                          delete validationErrors.clientAbbreviation;
                        }}
                        style={{
                          cursor: "pointer",
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      ></i>
                    ) : (
                      <i className="bi bi-search search-icon"></i>
                    )}

                    {clientOptions.length > 0 && !client && (
                      <ul className="dropdown-list">
                        {clientOptions.map((client) => (
                          <li
                            key={client.clientID}
                            onClick={() => {
                              setClient(client);
                              setSearchTerm(client.clientName);
                              setClientOptions([]);
                            }}
                          >
                            {client.clientName} ({client.diocese})
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Position Code:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      onChange={handlePositionCodeChange}
                      value={positionCode ?? ""}
                      disabled={client == null}
                    />
                  </div>
                </div>
                <div
                  className="field-stacked"
                  style={{ flex: "0 0 20%" }}
                ></div>

                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Client Name:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={client?.clientName ? client.clientName : ""}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">
                    Client Abbreviation/Number:
                  </label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={client?.abbreviation ? client.abbreviation : ""}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 40%" }}>
                  <label className="field-label">Primary Contact Name:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={`${client?.primaryFirstName || ""} ${
                        client?.primaryLastName || ""
                      }`}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                {validationErrors.clientAbbreviation && (
                  <div
                    className="invalid-feedback"
                    style={{ display: "block" }}
                  >
                    {validationErrors.clientAbbreviation}
                  </div>
                )}

                <div
                  className="text-container-header"
                  style={{ flex: "0 0 100%" }}
                >
                  Candidate(s)
                </div>
                {candidateOrders?.map((candidate, index) => (
                  <div
                    key={candidate.candidateOrderID}
                    className="additional-contact-group"
                  >
                    <div
                      className="text-sub-sub-header"
                      style={{ display: "flex", gap: "20px", flex: "0 0 100%" }}
                    >
                      <div> Candidate #{index + 1}</div>

                      <div>
                        <button
                          className="smbtn btn-red"
                          onClick={() =>
                            handleDeleteCandidateOrder(
                              candidate.candidateOrderID,
                            )
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                      <label className="field-label">Title:</label>
                      <div className="field-input">
                        <input
                          className={`form-control`}
                          type="text"
                          value={candidate.candidateTitle ?? ""}
                          onChange={(e) =>
                            handleInputChange(e, "candidateTitle", candidate)
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">First Name:</label>
                      <div className="field-input">
                        <input
                          className={`form-control ${
                            validationErrors[
                              `candidateFirstName-${candidate.candidateOrderID}`
                            ]
                              ? "is-invalid"
                              : ""
                          }`}
                          type="text"
                          value={candidate.candidateFirstName}
                          onChange={(e) =>
                            handleInputChange(
                              e,
                              "candidateFirstName",
                              candidate,
                            )
                          }
                        />
                        {validationErrors[
                          `candidateFirstName-${candidate.candidateOrderID}`
                        ] && (
                          <div className="invalid-feedback">
                            {
                              validationErrors[
                                `candidateFirstName-${candidate.candidateOrderID}`
                              ]
                            }
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">Middle Name:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={
                            candidate.candidateMI ? candidate.candidateMI : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "candidateMI", candidate)
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">Last Name:</label>
                      <div className="field-input">
                        <input
                          className={`form-control ${
                            validationErrors[
                              `candidateLastName-${candidate.candidateOrderID}`
                            ]
                              ? "is-invalid"
                              : ""
                          }`}
                          type="text"
                          value={candidate.candidateLastName}
                          onChange={(e) =>
                            handleInputChange(e, "candidateLastName", candidate)
                          }
                        />
                        {validationErrors[
                          `candidateLastName-${candidate.candidateOrderID}`
                        ] && (
                          <div className="invalid-feedback">
                            {
                              validationErrors[
                                `candidateLastName-${candidate.candidateOrderID}`
                              ]
                            }
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className="field-stacked"
                      style={{ flex: "0 0 20%" }}
                    ></div>
                    <div className="field-stacked" style={{ flex: "0 0 25%" }}>
                      <label className="field-label">SSN:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={
                            candidate.candidateSSN ? candidate.candidateSSN : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "candidateSSN", candidate)
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                      <label className="field-label">DOB:</label>
                      <MonthDayYearPicker
                        value={getSafePickerDate(candidate.candidateDOB)}
                        onChange={(newDate) =>
                          handleInputChange(
                            {
                              target: { value: toSafeDateString(newDate) },
                            } as any,
                            "candidateDOB",
                            candidate,
                          )
                        }
                      />
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                      <label className="field-label">Phone:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={
                            candidate.candidatePhone
                              ? candidate.candidatePhone
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "candidatePhone", candidate)
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">Address:</label>
                      <div className="field-input">
                        <input
                          className={`form-control ${
                            validationErrors[
                              `candidateAddress-${candidate.candidateOrderID}`
                            ]
                              ? "is-invalid"
                              : ""
                          }`}
                          type="text"
                          value={
                            candidate.candidateAddress
                              ? candidate.candidateAddress
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "candidateAddress", candidate)
                          }
                        />
                        {validationErrors[
                          `candidateAddress-${candidate.candidateOrderID}`
                        ] && (
                          <div className="invalid-feedback">
                            {
                              validationErrors[
                                `candidateAddress-${candidate.candidateOrderID}`
                              ]
                            }
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                      <label className="field-label">Address 2:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={
                            candidate.candidateAddress2
                              ? candidate.candidateAddress2
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "candidateAddress2", candidate)
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">City:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={
                            candidate.candidateCity
                              ? candidate.candidateCity
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "candidateCity", candidate)
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">State:</label>
                      <select
                        className="form-select"
                        value={
                          candidate.candidateState
                            ? candidate.candidateState
                            : ""
                        }
                        onChange={(e) =>
                          handleInputChange(e, "candidateState", candidate)
                        }
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option
                            key={state.abbreviation}
                            value={state.abbreviation}
                          >
                            {state.name} ({state.abbreviation})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                      <label className="field-label">Zip Code:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={
                            candidate.candidateZipCode
                              ? candidate.candidateZipCode
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "candidateZipCode", candidate)
                          }
                        />
                      </div>
                    </div>
                    <div
                      className="field-stacked"
                      style={{
                        flex: "0 0 50%",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div style={{ flex: "0 0 100%" }}>
                        <div style={{ display: "flex", gap: "5px" }}>
                          <label className="field-label">Email:</label>
                          <ToolTip
                            tooltipText="This email is used for login purposes for the candidate's online portal. If using a new email, a profile will be created for them to access their portal."
                            placement="right"
                            className="info-icon-spec"
                          ></ToolTip>
                        </div>
                        <div className="field-input">
                          <input
                            className={`form-control ${
                              validationErrors[
                                `candidateEmail-${candidate.candidateOrderID}`
                              ]
                                ? "is-invalid"
                                : ""
                            }`}
                            type="text"
                            value={
                              candidate.candidateEmail
                                ? candidate.candidateEmail
                                : ""
                            }
                            onChange={(e) =>
                              handleInputChange(e, "candidateEmail", candidate)
                            }
                          />
                          {validationErrors[
                            `candidateEmail-${candidate.candidateOrderID}`
                          ] && (
                            <div className="invalid-feedback">
                              {
                                validationErrors[
                                  `candidateEmail-${candidate.candidateOrderID}`
                                ]
                              }
                            </div>
                          )}
                        </div>
                      </div>
                      <i
                        onClick={() => handleCheckCandidateEmail(candidate)}
                        className="bi bi-search search-icon"
                        style={{ paddingTop: "20px", cursor: "pointer" }}
                      ></i>
                      <div
                        style={{
                          flex: "0 0 40%",
                          marginTop: "20px",
                          justifyContent: "center",
                        }}
                      >
                        <div>{candidate.newEmailText}</div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="btn-center" style={{ flex: "0 0 100%" }}>
                  <button
                    className={`oxbtn btn-purple ${
                      saveDisabled ? "disabled" : ""
                    }`}
                    onClick={handleAddAnotherCandidateOrder}
                    disabled={saveDisabled}
                  >
                    Add A Candidate
                    <i className="bi bi-plus-lg btn-icon"></i>
                  </button>
                </div>

                <div
                  className="page-items-container"
                  style={{ flex: "0 0 100%" }}
                >
                  <div className="text-container-header">Services</div>
                </div>
                <div className="services-list">
                  {services.map((service) => (
                    <div key={service.serviceID} className="service-item">
                      <label>
                        <input
                          style={{ marginRight: "10px" }}
                          className={`form-check-input`}
                          type="checkbox"
                          checked={orderServices.includes(service.serviceID)}
                          onChange={() =>
                            handleCheckboxChange(service.serviceID)
                          }
                        />
                        {service.serviceName}
                      </label>
                    </div>
                  ))}
                </div>
                {validationErrors.orderServices && (
                  <div
                    className="invalid-feedback"
                    style={{ display: "block" }}
                  >
                    {validationErrors.orderServices}
                  </div>
                )}

                <div
                  className="page-items-container"
                  style={{ flex: "0 0 100%" }}
                >
                  <div className="text-container-header">Order</div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Initiated Date:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="date"
                      value={formatDate(order.initiatedDate)}
                      onChange={(e) => handleInputChange(e, "initiatedDate")}
                      disabled
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Initial Forms Date:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="date"
                      value={formatDate(order.initialFormsDate)}
                      onChange={(e) => handleInputChange(e, "initialFormsDate")}
                      disabled
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Received Date:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="date"
                      value={formatDate(order.receivedDate)}
                      onChange={(e) => handleInputChange(e, "receivedDate")}
                      disabled
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Completed Date:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="date"
                      value={formatDate(order.completedDate)}
                      onChange={(e) => handleInputChange(e, "completedDate")}
                      disabled
                    />
                  </div>
                </div>
                <div style={{ flex: "0 0 10%" }}></div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <div style={{ display: "flex", gap: "5px" }}>
                    <label className="field-label">Sent By Mail:</label>
                    <ToolTip
                      tooltipText="If sent by mail selected and email provided, candidate can login to online portal but forms will not be available."
                      placement="right"
                      className="info-icon-spec"
                    ></ToolTip>
                  </div>
                  <div>
                    <input
                      className={`form-check-input`}
                      type="checkbox"
                      checked={order.sendByMail}
                      onChange={(e) => handleInputChange(e, "sendByMail")}
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">FCRA Compliance:</label>
                  <div>
                    <input
                      className={`form-check-input`}
                      type="checkbox"
                      checked={order.fcraCompliance}
                      onChange={(e) => handleInputChange(e, "fcraCompliance")}
                    />
                  </div>
                </div>
                <div style={{ flex: "0 0 80%" }}></div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Invoice Process Type:</label>
                  <div
                    style={{
                      flex: "0 0 30%",
                      alignSelf: "end",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <select
                      className={`form-select`}
                      value={order.invoiceProcessTypeID || ""}
                      onChange={(e) =>
                        handleInputChange(e, "invoiceProcessTypeID")
                      }
                    >
                      {invoiceProcessTypes.map((type) => (
                        <option
                          key={type.invoiceProcessTypeID}
                          value={type.invoiceProcessTypeID}
                        >
                          {type.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 60%" }}>
                  <label className="field-label">Designation:</label>
                  <div className="field-input">
                    <input
                      className={`form-control ${
                        validationErrors.designation ? "is-invalid" : ""
                      }`}
                      type="text"
                      value={order.designation ? order.designation : ""}
                      onChange={(e) => handleInputChange(e, "designation")}
                    />
                    {validationErrors.designation && (
                      <div className="invalid-feedback">
                        {validationErrors.designation}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ flex: "0 0 100%" }}></div>

                {orderServices.some((serviceID) =>
                  [1, 2, 3].includes(serviceID),
                ) && (
                  <>
                    {" "}
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">Reference Type:</label>
                      <div
                        style={{
                          flex: "0 0 30%",
                          alignSelf: "end",
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        <select
                          className={`form-select`}
                          value={referenceOrder?.referenceTypeID || ""}
                          onChange={(e) =>
                            handleInputChange(e, "referenceTypeID")
                          }
                        >
                          {referenceTypes.map((type) => (
                            <option
                              key={type.referenceTypeID}
                              value={type.referenceTypeID}
                            >
                              {type.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div
                      className="field-stacked"
                      style={{ flex: "0 0 70%" }}
                    ></div>
                    {referenceOrder.referenceTypeID === 1 && (
                      <>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 40%" }}
                        >
                          <label className="field-label">
                            Prior Client Name:
                          </label>
                          <div className="field-input">
                            <input
                              className={`form-control ${
                                validationErrors.priorClientName
                                  ? "is-invalid"
                                  : ""
                              }`}
                              type="text"
                              value={
                                referenceOrder?.priorClientName
                                  ? referenceOrder?.priorClientName
                                  : ""
                              }
                              onChange={(e) =>
                                handleInputChange(e, "priorClientName")
                              }
                            />
                            {validationErrors.priorClientName && (
                              <div className="invalid-feedback">
                                {validationErrors.priorClientName}
                              </div>
                            )}
                          </div>
                        </div>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 30%" }}
                        >
                          <label className="field-label">Prior Date:</label>
                          <div className="field-input">
                            <MonthDayYearPicker
                              value={getSafePickerDate(
                                referenceOrder.priorDate,
                              )}
                              onChange={(newDate) =>
                                handleInputChange(
                                  {
                                    target: {
                                      value: toSafeDateString(newDate),
                                    },
                                  } as any,
                                  "priorDate",
                                )
                              }
                            />
                            {validationErrors.priorDate && (
                              <div className="invalid-feedback">
                                {validationErrors.priorDate}
                              </div>
                            )}
                          </div>
                        </div>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 10%" }}
                        >
                          <label className="field-label">
                            Response Tracking Received:
                          </label>
                          <div className="field-input">
                            <input
                              className="form-control"
                              type="text"
                              value={
                                referenceOrder?.responseTrackingReceived
                                  ? referenceOrder?.responseTrackingReceived
                                  : ""
                              }
                              onChange={(e) =>
                                handleInputChange(e, "responseTrackingReceived")
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}
                    <div
                      className="field-stacked"
                      style={{ flex: "0 0 100%" }}
                    ></div>
                    <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                      <label className="field-label">
                        <b> Reference Order Invoice Amount:</b>
                      </label>
                      <div className="field-input">
                        <input
                          className={`form-control ${
                            validationErrors.invoiceAmountCheck
                              ? "is-invalid"
                              : ""
                          }`}
                          type="text"
                          value={
                            referenceOrder.invoiceAmount
                              ? referenceOrder.invoiceAmount
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "invoiceAmount")
                          }
                          disabled={order.orderStatusID === 4}
                        />
                        {validationErrors.invoiceAmountCheck && (
                          <div className="invalid-feedback">
                            {validationErrors.invoiceAmountCheck}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                      <label className="field-label">
                        Confirm Invoice Amount:{" "}
                      </label>
                      <div>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={invoiceAmountCheck}
                          onChange={() => {
                            setInvoiceAmountCheck(!invoiceAmountCheck);
                            setValidationErrors((prevErrors) => ({
                              ...prevErrors,
                              invoiceAmountCheck: "",
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
                {orderServices.some((serviceID) =>
                  [
                    4, 5, 6, 9, 10, 11, 12, 13, 14, 15, 16, 17, 23, 30, 31, 32,
                    34,
                  ].includes(serviceID),
                ) && (
                  <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                    <label className="field-label">
                      <b> Criminal LookBack Years:</b>
                    </label>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="text"
                        value={criminalOrder.years ? criminalOrder.years : ""}
                        onChange={(e) => handleCriminalInputChange(e, "years")}
                        disabled={order.orderStatusID === 4}
                      />
                    </div>
                  </div>
                )}
                <div style={{ flex: "0 0 100%" }}></div>

                {saveDisabled && (
                  <div className="error" style={{ flex: "0 0 100%" }}>
                    Only one candidate allowed when creating Transfer orders.
                  </div>
                )}

                <div className="btn-center" style={{ flex: "0 0 100%" }}>
                  <button
                    className={`oxbtn btn-green ${
                      !client?.clientID || candidateOrders.length === 0
                        ? "disabled"
                        : ""
                    }`}
                    onClick={() => handleSave()}
                    disabled={!client?.clientID || candidateOrders.length === 0}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default CreateOrder;
